import { useState } from "react";
import { theme, FONT_RUNE } from "../theme";
import * as S from "./sheet";

/**
 * One-time recovery-code modal. Shown after a successful signup (or recovery rotation).
 * The user can't proceed without explicitly confirming they've saved the code — once this
 * modal closes, the raw code is gone forever.
 */
export function RecoveryCodeModal({
  code,
  context,
  onConfirm,
}: {
  code: string;
  context: "signup" | "rotate";
  onConfirm: () => void;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      /* user can still copy manually */
    }
  };

  return (
    <div style={{ ...S.overlay, zIndex: 250 }}>
      <div style={{ ...S.card, maxWidth: 460 }}>
        <div style={S.header}>
          <span style={S.title}>Save your recovery code</span>
        </div>
        <div style={S.body}>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, opacity: 0.92 }}>
            {context === "signup"
              ? "We don't use email for password resets. This code is the only way to recover your account if you forget your password — save it somewhere you'll find it again (password manager, notes app, paper)."
              : "Your old recovery code is now invalid. Save this new one — same rules: keep it somewhere safe, you won't see it again."}
          </p>

          <div
            style={{
              fontFamily: FONT_RUNE,
              fontSize: 18,
              letterSpacing: 2,
              padding: "14px 12px",
              background: theme.bg,
              color: theme.accent,
              border: `1px solid ${theme.accent}`,
              borderRadius: 6,
              textAlign: "center",
              wordBreak: "break-all",
            }}
          >
            {code}
          </div>

          <button onClick={copy} style={S.secondaryButton}>
            {copied ? "Copied" : "Copy to clipboard"}
          </button>

          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14, lineHeight: 1.5 }}>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span>I've saved this code somewhere I can find it again.</span>
          </label>

          <button
            disabled={!confirmed}
            onClick={onConfirm}
            style={{
              ...S.primaryButton,
              opacity: confirmed ? 1 : 0.4,
              cursor: confirmed ? "pointer" : "not-allowed",
            }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
